import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';
import type { KitchenBlueprintInput } from '../types';
import type { SceneComposition, SceneObject } from '../../../types/engine.types';
import {
	getFloorMaterialCfn,
	loadCriticalTexturesCfn,
	loadSceneMaterialsCfn,
	loadApplianceTexturesCfn,
	queueBackgroundPreloadsCfn
} from './load-textures.cfn';
import { analyzeTextureRequirementsCfn } from './analyze-texture-requirements.cfn';
import { buildSceneManifestCfn } from './build-scene-manifest.cfn';
import { createIslandGroupCfn } from './create-island.cfn';
import { createKitchenGeometryCfn } from './create-geometry.cfn';
import { generateCutoutsCfn } from './generate-cutouts.cfn';
import { loadAppliancesCfn } from './load-appliances.cfn';
import { calculateKitchenBoundsCfn, calculateCameraConfigCfn } from './camera-bounds.cfn';

// Three's module-level FileLoader dedup can piggyback a fresh engine's texture request onto a dead
// engine's never-resolving fetch — an unbounded await then deadlocks the whole build with no error.
const boundedLoad = async (label: string, load: Promise<void>, timeoutMs = 30_000): Promise<void> => {
	let timer: ReturnType<typeof setTimeout> | undefined;
	const timeout = new Promise<void>((resolve) => {
		timer = setTimeout(() => {
			console.warn(`[KitchenSceneBuilder] ${label} exceeded ${timeoutMs}ms — proceeding without waiting (deadlocked shared fetch?)`);
			resolve();
		}, timeoutMs);
	});
	await Promise.race([load, timeout]);
	clearTimeout(timer);
};

/**
 * Build scene composition from kitchen blueprint.
 * Uses phased texture loading: critical (floor+sky) first, then scene materials,
 * then appliance textures, with background preloads queued last.
 */
export const buildCfn = async (
	self: KitchenSceneBuilder,
	{ input }: { input: KitchenBlueprintInput }
): Promise<SceneComposition> => {
	const objects: SceneObject[] = [];

	// Step 1: Initialize kitchen data
	if (input.kitchen) {
		self.kitchenDataManagerAccess.init(input.kitchen);
	} else {
		console.warn('KitchenSceneBuilder.build: No kitchen data provided!');
	}

	self.currentManifestAccess = buildSceneManifestCfn(self, { input });

	// Step 2: Analyze texture requirements for phased loading
	const requirements = analyzeTextureRequirementsCfn(self, { input });

	// Step 3: Load critical textures (floor + sky)
	await boundedLoad('loadCriticalTextures', loadCriticalTexturesCfn(self, {
		floorKey: requirements.critical.floor,
		skyKey: requirements.critical.sky
	}));

	// Step 4: Load scene materials (surround, countertop, trim)
	await boundedLoad('loadSceneMaterials', loadSceneMaterialsCfn(self, {
		sceneMaterials: requirements.sceneMaterials
	}));

	// Step 5: Load appliance textures (skipped when no appliances are placed)
	await boundedLoad('loadApplianceTextures', loadApplianceTexturesCfn(self, { modelRequirements: requirements.models }));

	// Step 6: Create island containers
	const islands = self.kitchenDataManagerAccess.getIslands();

	if (islands.length === 0) {
		console.warn('KitchenSceneBuilder.build: No islands found! Kitchen will be empty.');
	}

	self.clearIslandGroups();

	for (let i = 0; i < islands.length; i++) {
		const island = islands[i];
		const islandGroup = createIslandGroupCfn(self, { island, index: i });
		self.addIslandGroup(islandGroup);

		objects.push({
			object: islandGroup,
			metadata: {
				id: island.id ?? `island-${i}`,
				type: 'island',
				source: self.name
			}
		});
	}

	// Step 7: Create floor
	const floorMaterial = getFloorMaterialCfn(self, { floorKey: input.floorKey });
	const floor = self.geometryBuilderAccess.createFloor(floorMaterial);
	objects.push({
		object: floor,
		metadata: {
			id: 'floor',
			type: 'floor',
			source: self.name
		}
	});

	// Step 8: Create kitchen geometry (panels, trims, countertops)
	await createKitchenGeometryCfn(self, {});

	// Step 9: Generate cutouts
	if (input.controller?.options?.showCutouts !== false) {
		const debugGroup = await generateCutoutsCfn(self, {});
		if (debugGroup) {
			objects.push({
				object: debugGroup,
				metadata: {
					id: 'cutout-debug-group',
					type: 'debug',
					source: self.name
				}
			});
		}
	}

	// Step 10: Load appliance models
	if (input.controller?.options?.showAppliances !== false) {
		await loadAppliancesCfn(self, { input });
	}

	// Step 11: Set pending sky upgrades (deferred until after models are loaded)
	if (requirements.qualityUpgrades.length > 0) {
		(self as any)._pendingSkyUpgrades = requirements.qualityUpgrades;
	}

	// Step 12: Queue background preloads for instant switching
	queueBackgroundPreloadsCfn(self, { input, requirements });

	// Calculate bounds and camera position
	const kitchenSize = input.kitchen?.size ?? { width: 96, depth: 36, height: 50 };
	const bounds = calculateKitchenBoundsCfn(self, { kitchenSize });
	const camera = calculateCameraConfigCfn(self, { kitchenSize, zoom: input.controller?.viewport?.state?.zoom ?? 1.0 });

	return {
		objects,
		bounds,
		camera
	};
};
